// BehaviorPanel.jsx – Dynamic behavior criteria buttons + undo for the active student.
// Criteria are loaded from the backend; each can only be used ONCE per student per day.
import React, { useState } from 'react';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Undo from '@mui/icons-material/Undo';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';

// Fallback color palette for criteria (cycles if more than 6)
const PALETTE = [
  { color: '#e3f2fd', activeColor: '#90caf9', highlightColor: '#42a5f5', highlightBg: '#bbdefb' },
  { color: '#e8f5e9', activeColor: '#a5d6a7', highlightColor: '#43a047', highlightBg: '#c8e6c9' },
  { color: '#fff3e0', activeColor: '#ffcc80', highlightColor: '#ef6c00', highlightBg: '#ffe0b2' },
  { color: '#fce4ec', activeColor: '#f48fb1', highlightColor: '#c2185b', highlightBg: '#f8bbd0' },
  { color: '#e8eaf6', activeColor: '#9fa8da', highlightColor: '#3949ab', highlightBg: '#c5cae9' },
  { color: '#fff8e1', activeColor: '#ffe082', highlightColor: '#f9a825', highlightBg: '#ffecb3' },
];

function paletteFor(idx) {
  return PALETTE[idx % PALETTE.length];
}

/**
 * @param {{
 *   criteria: Array<{id: number, name: string, icon?: string}>,
 *   todayUsedCriteriaIds: number[],
 *   onBehaviorClick: (criteriaId: number, buttonElement: HTMLElement) => void,
 *   onUndo: () => void,
 *   undoDisabled: boolean,
 *   studentName: string,
 *   onAddCriteria?: (name: string) => void,
 * }} props
 */
const BehaviorPanel = ({
  criteria = [],
  todayUsedCriteriaIds = [],
  onBehaviorClick,
  onUndo,
  undoDisabled,
  studentName,
  onAddCriteria,
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCriteriaName, setNewCriteriaName] = useState('');

  const usedSet = new Set(todayUsedCriteriaIds);

  const handleAdd = () => {
    const name = newCriteriaName.trim();
    if (!name || !onAddCriteria) return;
    onAddCriteria(name);
    setNewCriteriaName('');
    setShowAddForm(false);
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: 4,
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        my: { md: 'auto' },
        gap: 1.5,
        bgcolor: '#fafafa',
        minWidth: { xs: 'auto', md: 280 },
        maxWidth: 360,
        width: '100%',
      }}
    >
      {/* Panel title */}
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          fontSize: 'clamp(16px, 1.8vw, 22px)',
          textAlign: 'center',
        }}
      >
        ⭐ Bugün Neler Yaptı?
      </Typography>

      <Typography
        variant="body2"
        sx={{
          textAlign: 'center',
          color: 'text.secondary',
          fontSize: 'clamp(12px, 1.2vw, 15px)',
          mb: 0.5,
        }}
      >
        {studentName}
      </Typography>

      <Divider />

      {/* Criteria buttons (dynamic from backend) */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
        {criteria.map((c, idx) => {
          const used = usedSet.has(c.id);
          const pal = paletteFor(idx);
          return (
            <Button
              key={c.id}
              variant="contained"
              disabled={used}
              onClick={(e) => onBehaviorClick(c.id, e.currentTarget)}
              sx={{
                minHeight: { xs: 64, md: 72 },
                borderRadius: 3,
                textTransform: 'none',
                fontSize: 'clamp(13px, 1.3vw, 17px)',
                fontWeight: 600,
                display: 'flex',
                gap: 1.5,
                justifyContent: 'flex-start',
                px: 3,
                bgcolor: used ? pal.highlightBg : pal.color,
                color: used ? pal.highlightColor : 'text.primary',
                boxShadow: used
                  ? `0 0 0 2px ${pal.highlightColor}40, 0 2px 8px ${pal.highlightColor}30`
                  : 2,
                border: used
                  ? `2px solid ${pal.highlightColor}`
                  : '2px solid transparent',
                '&:hover': { bgcolor: pal.activeColor },
                '&.Mui-disabled': {
                  bgcolor: pal.highlightBg,
                  color: pal.highlightColor,
                  border: `2px solid ${pal.highlightColor}`,
                  opacity: 1,
                },
                transition: 'all 300ms ease',
              }}
            >
              {used ? (
                <CheckCircleIcon
                  sx={{ fontSize: 'clamp(22px, 2.5vw, 32px)', color: pal.highlightColor }}
                />
              ) : (
                <Box
                  component="span"
                  sx={{ fontSize: 'clamp(20px, 2.5vw, 30px)', lineHeight: 1 }}
                >
                  {c.icon || '⭐'}
                </Box>
              )}
              <Box sx={{ flex: 1, textAlign: 'left' }}>{c.name}</Box>
            </Button>
          );
        })}
      </Box>

      {/* Add custom criteria */}
      {onAddCriteria && (
        <Box sx={{ mt: 0.5 }}>
          {!showAddForm ? (
            <Button
              size="small"
              startIcon={<AddCircleOutline />}
              onClick={() => setShowAddForm(true)}
              sx={{
                textTransform: 'none',
                fontSize: 'clamp(11px, 1.1vw, 14px)',
                color: 'text.secondary',
              }}
            >
              Yeni kriter ekle
            </Button>
          ) : (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Kriter adı..."
                value={newCriteriaName}
                onChange={(e) => setNewCriteriaName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                autoFocus
                sx={{ flex: 1 }}
              />
              <Button
                size="small"
                variant="contained"
                onClick={handleAdd}
                disabled={!newCriteriaName.trim()}
                sx={{ textTransform: 'none', minWidth: 60 }}
              >
                Ekle
              </Button>
              <Tooltip title="İptal">
                <IconButton size="small" onClick={() => { setShowAddForm(false); setNewCriteriaName(''); }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      )}

      <Divider sx={{ mt: 1 }} />

      {/* Undo button */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
        <Button
          variant="outlined"
          startIcon={<Undo />}
          disabled={undoDisabled}
          onClick={onUndo}
          sx={{
            textTransform: 'none',
            borderRadius: 3,
            fontSize: 'clamp(13px, 1.2vw, 16px)',
            minHeight: 48,
            px: 3,
          }}
        >
          🔄 Geri Al
        </Button>
      </Box>
    </Paper>
  );
};

export default BehaviorPanel;
